import React, { useState, useRef, useCallback, useEffect } from "react";
import { IonButton, IonIcon, IonInput, useIonToast, useIonAlert } from "@ionic/react";
import { addCircleOutline, refreshCircle, peopleOutline, keypadOutline } from "ionicons/icons";
import { Controller, FieldValues, useForm } from "react-hook-form";
import CommonFormElement from "../../components/UI/CommonFormElements/CommonFormElement";
import { CustomPhoneInput } from "../../components/CustomPhoneInput/CustomPhoneInput";
import commonFormStyles from "../../components/UI/CommonFormElements/CommonFormElements.module.scss";
import classNames from "classnames/bind";
import styles from "./InvitationForm.module.scss";
import { useContacts } from "../../hooks";
import { ContactList } from "../../components/ContactList/ContactList";
import { showSharingOptions } from "../../utils/shareApp";
import { useConfig } from "../../providers/config";
import { useTranslation } from "react-i18next";

const commonForm = classNames.bind(commonFormStyles);

export interface InvitationFormProps {
  onInviteSent: (data: { inviteePhone: string }) => Promise<boolean>;
  isLoading?: boolean;
}

const MAX_DISPLAYED_CONTACTS = 4;

export const InvitationForm: React.FC<InvitationFormProps> = ({ onInviteSent, isLoading = false }) => {
  const config = useConfig();
  const { t } = useTranslation();

  const [presentToast] = useIonToast();
  const [presentAlert] = useIonAlert();

  const handleContactsError = useCallback(
    (errorType: "permission" | "fetch") => {
      const message = errorType === "permission" ? t("contactsPermissionRequired") : t("errorFetchingContacts");
      presentToast({
        message,
        duration: 3000,
      });
    },
    [t, presentToast],
  );

  const { contacts } = useContacts(handleContactsError);

  const { handleSubmit, control, reset } = useForm<FieldValues>({
    mode: "onSubmit",
  });

  const [isPhoneValid, setIsPhoneValid] = useState(false);
  const [filteredContacts, setFilteredContacts] = useState<any[]>([]);
  const [searchMode, setSearchMode] = useState<"phone" | "name">("name");
  const [invitedPhone, setInvitedPhone] = useState<string | null>(null);

  const phoneInputRef = useRef<HTMLInputElement>(null);
  const nameInputRef = useRef<HTMLIonInputElement>(null);
  const formRef = useRef<HTMLFormElement>(null);

  // Add this useEffect hook to handle focus changes
  useEffect(() => {
    const focusInput = async () => {
      // Add a delay of 100ms before focusing
      await new Promise((resolve) => setTimeout(resolve, 100));

      if (searchMode === "phone") {
        phoneInputRef.current?.focus();
      } else {
        await nameInputRef.current?.setFocus();
      }
    };
    focusInput();
  }, [searchMode]);

  useEffect(() => {
    if (filteredContacts.length > 0) {
      formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [filteredContacts]);

  const handleInvite = async (data: FieldValues) => {
    if (!data.inviteePhone || !isPhoneValid || searchMode !== "phone") return;

    try {
      if (await onInviteSent({ inviteePhone: data.inviteePhone })) {
        // After successful invitation, show sharing options
        await showSharingOptions(data.inviteePhone, config, t, presentAlert);
      }

      reset({ inviteePhone: "" });
      setSearchMode("name");
    } catch (error) {
      console.error("Error sending invitation:", error);
      presentToast({
        message: t("errorSendingInvitation"),
        duration: 3000,
        color: "danger",
      });
    }
  };

  const handleInputChange = (value: string, isValid?: boolean) => {
    if (searchMode === "phone") {
      setIsPhoneValid(isValid === true);
    } else {
      setIsPhoneValid(false); // Ensure phone is not valid in name mode
      // Handle name search
      if (value && contacts.length > 0) {
        const filtered = contacts.filter((contact) =>
          (contact?.name?.display + " " + contact?.phones?.[0]?.number).toLowerCase().includes(value.toLowerCase()),
        );
        setFilteredContacts(filtered);
      } else {
        setFilteredContacts([]);
      }
    }
  };

  const toggleSearchMode = () => {
    setSearchMode((prevMode) => (prevMode === "phone" ? "name" : "phone"));
    setFilteredContacts([]);
    reset({ inviteePhone: "" });
    setIsPhoneValid(false);
  };

  const handleSelectContact = (contact: any) => {
    if (contact && contact.phones && contact.phones.length > 0) {
      const phoneNumber = contact.phones?.[0].number || "";
      reset({ inviteePhone: phoneNumber });
      setFilteredContacts([]);
      setIsPhoneValid(true);
      setSearchMode("phone");
    } else {
      console.warn("Selected contact has no phone number");
    }
  };

  return (
    <div className={styles.formContainer}>
      <form ref={formRef} onSubmit={handleSubmit(handleInvite)} className={styles.form}>
        <div className={styles.inputButtonWrapper}>
          <div className={styles.inputWrapper}>
            <Controller
              name="inviteePhone"
              control={control}
              render={({ field: { onChange, value } }) => (
                <CommonFormElement isValid={isPhoneValid}>
                  {searchMode === "phone" ? (
                    <CustomPhoneInput
                      className={commonForm("commonElement", { commonInvalid: !isPhoneValid })}
                      value={value || ""}
                      onCustomChange={(val, isValid) => {
                        onChange(val);
                        handleInputChange(val, isValid);
                      }}
                      onEnterKeyPress={() => {
                        if (isPhoneValid) {
                          handleSubmit(handleInvite)();
                        }
                      }}
                      inputProps={{
                        ref: phoneInputRef,
                      }}
                    />
                  ) : (
                    <IonInput
                      className={commonForm("commonElement", { commonInvalid: !isPhoneValid })}
                      value={value || ""}
                      onIonChange={(e) => {
                        const val = e.detail.value || "";
                        onChange(val);
                        handleInputChange(val);
                      }}
                      placeholder={t("enterNameOrPhone")}
                      ref={nameInputRef}
                    />
                  )}
                  <IonButton fill="clear" className={styles.contactsButton} onClick={toggleSearchMode}>
                    <IonIcon icon={searchMode === "phone" ? peopleOutline : keypadOutline} />
                  </IonButton>
                </CommonFormElement>
              )}
            />
          </div>
          <IonButton
            className={styles.submitButton}
            type="submit"
            disabled={isLoading || !isPhoneValid || searchMode !== "phone"}
          >
            <IonIcon icon={isLoading ? refreshCircle : addCircleOutline} className={isLoading ? styles.spinIcon : ""} />
          </IonButton>
        </div>
      </form>
      {filteredContacts.length > 0 && searchMode === "name" && (
        <ContactList
          contacts={filteredContacts.slice(0, MAX_DISPLAYED_CONTACTS)}
          onSelectContact={handleSelectContact}
        />
      )}
    </div>
  );
};
